import { useState, useCallback } from 'react';
import { SvgImageName } from '../../components/ImagesComponents';

interface UseSvgIconOptions {
  preload?: boolean;
  cacheKey?: string;
}

interface UseSvgIconReturn {
  isLoading: boolean;
  hasError: boolean;
  loadIcon: (name: SvgImageName) => void;
  preloadIcon: (name: SvgImageName) => Promise<void>;
  clearCache: () => void;
}

const iconCache = new Map<string, boolean>();
const loadingStates = new Map<string, boolean>();

export const useSvgIcon = (
  options: UseSvgIconOptions = {}
): UseSvgIconReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  const loadIcon = useCallback((name: SvgImageName) => {
    if (iconCache.has(name)) {
      return;
    }

    setIsLoading(true);
    setHasError(false);
    setTimeout(() => {
      iconCache.set(name, true);
      setIsLoading(false);
    }, 100);
  }, []);

  const preloadIcon = useCallback(
    async (name: SvgImageName): Promise<void> => {
      return new Promise(resolve => {
        if (iconCache.has(name)) {
          resolve();
          return;
        }

        loadIcon(name);

        const checkLoaded = () => {
          if (iconCache.has(name)) {
            resolve();
          } else {
            setTimeout(checkLoaded, 50);
          }
        };

        checkLoaded();
      });
    },
    [loadIcon]
  );

  const clearCache = useCallback(() => {
    iconCache.clear();
    loadingStates.clear();
  }, []);

  return {
    isLoading,
    hasError,
    loadIcon,
    preloadIcon,
    clearCache,
  };
};

export const getAvailableSvgNames = (): SvgImageName[] => [
  'logomark_purple',
  'spy_view',
  'server',
  'login',
  'router',
  'chat',
  'search',
  'cloud_privacy',
  'alert',
  'team_img_1',
  'team_img_2',
  'team_img_3',
  'team_img_4',
  'logo_menu',
  'contact_email_img',
  'contact_location_img',
  'contact_phone_img',
];

export const isValidSvgName = (name: string): name is SvgImageName => {
  return getAvailableSvgNames().includes(name as SvgImageName);
};
